import { configForLivePreview } from './preview_config';
import { generatePageHtml } from './generate_page_html';
import { getEnabledPortalFields } from './portal_fields';
import { headerToKey } from './keys';

/**
 * @param {string} text
 */
function escHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {string} header
 * @param {string} key
 */
function sampleValueForColumn(header, key) {
  const h = String(header || key).toLowerCase();

  if (/status/.test(h)) return 'Checked Out';
  if (/email/.test(h)) return 'alex@example.com';
  if (/phone|mobile|tel/.test(h)) return '(555) 123-4567';
  if (/date|due|ship/.test(h)) return 'May 15, 2026';
  if (/\b(id|sku|code|tracking|patron|invoice|po)\b/.test(h)) return 'DEMO-001';
  if (/name|client|author|title|book/.test(h)) return 'Alex Morgan';
  if (/url|link|website/.test(h)) return 'https://example.com';
  if (/qty|quantity|count|amount|price/.test(h)) return '12';
  if (/address|city|state|zip/.test(h)) return '123 Main St';
  if (/carrier/.test(h)) return 'UPS';
  if (/item|product|shipped/.test(h)) return 'Widget Pro (×2)';

  const label = String(header || key).trim();
  return label ? `Sample ${label}` : 'Sample value';
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 * @param {{ found?: boolean }} [options]
 */
export function buildSampleData(config, options = {}) {
  const found = options.found !== false;
  const data = {
    found,
    webappUrl: '#',
    postUrl: '#',
    patronId: 'PATRON-42',
    status: found ? 'Active' : 'Not found',
    inQueue: false,
    displayTrackingLink: 'https://www.ups.com/track?tracknum=1Z999AA10123456784',
    client: 'Acme Corp',
    searchNumber: 'INV-2026-0042',
    poNo: 'PO-8812',
    invoiceNo: 'INV-9901',
    displayStatus: 'In transit',
    carrier: 'UPS',
    displayTrackingNo: '1Z999AA10123456784',
    shippedItems: 'Widget Pro (×2), Cable kit (×1)',
    backOrder: 'None',
    loans: [
      {
        bookTitle: 'The Pragmatic Programmer',
        author: 'Hunt & Thomas',
        status: 'Checked Out',
        dueDate: 'Jun 12, 2026',
        checkedOutDate: 'May 1, 2026',
        shelfLocation: 'A-12',
        checkoutId: 'CHK-1001'
      },
      {
        bookTitle: 'Designing Data-Intensive Applications',
        author: 'Martin Kleppmann',
        status: 'Checked Out',
        dueDate: 'Jun 20, 2026',
        checkedOutDate: 'May 3, 2026',
        shelfLocation: 'B-04',
        checkoutId: 'CHK-1002'
      }
    ]
  };

  for (const field of getEnabledPortalFields(config)) {
    data[field.columnKey] = sampleValueForColumn(field.label, field.columnKey);
  }

  for (const col of config.sheet?.columns || []) {
    const key = col.key || headerToKey(col.header);
    if (data[key] === undefined) {
      data[key] = sampleValueForColumn(col.header, key);
    }
  }

  return data;
}

/**
 * @param {string} expr
 * @param {{ data: Record<string, unknown>, loan: Record<string, unknown> | null }} ctx
 */
function evalExpr(expr, ctx) {
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('data', 'loan', `return (${expr});`);
    const value = fn(ctx.data, ctx.loan);
    return value == null ? '' : value;
  } catch {
    return '';
  }
}

/**
 * @param {string} inner
 */
function isBlockOpen(inner) {
  return inner.startsWith('if ') || /^for\s*\(/.test(inner);
}

/**
 * @param {string} src
 * @param {number} bodyStart
 * @returns {{ elseAt: number, endAt: number }}
 */
function findConditionalBlockEnd(src, bodyStart) {
  let depth = 0;
  let elseAt = -1;
  let endAt = -1;
  let i = bodyStart;

  while (i < src.length) {
    const tagStart = src.indexOf('<?', i);
    if (tagStart === -1) break;

    const tagEnd = src.indexOf('?>', tagStart);
    if (tagEnd === -1) break;

    const inner = src.slice(tagStart + 2, tagEnd).trim();
    const afterTag = tagEnd + 2;

    if (isBlockOpen(inner)) {
      depth += 1;
    } else if (inner === '} else {' && depth === 0) {
      elseAt = tagStart;
    } else if (inner === '}') {
      if (depth === 0) {
        endAt = tagStart;
        break;
      }
      depth -= 1;
    }

    i = afterTag;
  }

  return { elseAt, endAt };
}

/**
 * @param {string} src
 * @param {number} bodyStart
 * @returns {number}
 */
function findLoopBlockEnd(src, bodyStart) {
  let depth = 0;
  let i = bodyStart;

  while (i < src.length) {
    const tagStart = src.indexOf('<?', i);
    if (tagStart === -1) break;

    const tagEnd = src.indexOf('?>', tagStart);
    if (tagEnd === -1) break;

    const inner = src.slice(tagStart + 2, tagEnd).trim();

    if (isBlockOpen(inner)) {
      depth += 1;
    } else if (inner === '}') {
      if (depth === 0) return tagStart;
      depth -= 1;
    }

    i = tagEnd + 2;
  }

  return -1;
}

/**
 * @param {string} src
 * @param {number} start
 * @param {{ data: Record<string, unknown>, loan: Record<string, unknown> | null }} ctx
 */
function processFrom(src, start, ctx) {
  let out = '';
  let i = start;

  while (i < src.length) {
    const tagStart = src.indexOf('<?', i);
    if (tagStart === -1) {
      out += src.slice(i);
      break;
    }

    out += src.slice(i, tagStart);
    const tagEnd = src.indexOf('?>', tagStart);
    if (tagEnd === -1) {
      out += src.slice(tagStart);
      break;
    }

    const inner = src.slice(tagStart + 2, tagEnd).trim();
    const bodyStart = tagEnd + 2;

    if (inner.startsWith('=')) {
      out += escHtml(String(evalExpr(inner.slice(1).trim(), ctx)));
      i = bodyStart;
      continue;
    }

    if (inner.startsWith('if ')) {
      const cond = inner.slice(3, inner.lastIndexOf('{')).trim();
      const { elseAt, endAt } = findConditionalBlockEnd(src, bodyStart);

      if (endAt === -1) {
        out += src.slice(tagStart);
        break;
      }

      const condTrue = Boolean(evalExpr(cond, ctx));
      const elseMarker = '<? } else { ?>';
      const endMarker = '<? } ?>';

      if (elseAt !== -1) {
        const trueHtml = src.slice(bodyStart, elseAt);
        const falseHtml = src.slice(elseAt + elseMarker.length, endAt);
        out += processFrom(condTrue ? trueHtml : falseHtml, 0, ctx);
        i = endAt + endMarker.length;
      } else {
        const trueHtml = src.slice(bodyStart, endAt);
        if (condTrue) out += processFrom(trueHtml, 0, ctx);
        i = endAt + endMarker.length;
      }
      continue;
    }

    const forMatch = inner.match(
      /^for\s*\(\s*var\s+(\w+)\s*=\s*0\s*;\s*\1\s*<\s*data\.loans\.length\s*;\s*\1\+\+\s*\)\s*\{\s*$/
    );
    if (forMatch) {
      const endAt = findLoopBlockEnd(src, bodyStart);
      if (endAt === -1) {
        out += src.slice(tagStart);
        break;
      }

      const body = src.slice(bodyStart, endAt);
      const loans = Array.isArray(ctx.data.loans) ? ctx.data.loans : [];

      for (let li = 0; li < loans.length; li++) {
        const loan = loans[li];
        out += processFrom(body, 0, { data: ctx.data, loan });
      }

      i = endAt + '<? } ?>'.length;
      continue;
    }

    const varMatch = inner.match(/^var\s+(\w+)\s*=\s*(.+);$/);
    if (varMatch?.[1] === 'loan') {
      i = bodyStart;
      continue;
    }

    i = bodyStart;
  }

  return out;
}

/**
 * @param {string} pageHtml
 * @param {{ data: Record<string, unknown> }} ctx
 */
export function renderAppsScriptTemplate(pageHtml, ctx) {
  return processFrom(pageHtml, 0, { data: ctx.data, loan: null });
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 * @param {{ found?: boolean }} [options]
 * @returns {string}
 */
export function renderPagePreview(config, options = {}) {
  const { config: previewConfig } = configForLivePreview(config);
  const pageHtml = generatePageHtml(previewConfig);
  const data = buildSampleData(previewConfig, options);
  return renderAppsScriptTemplate(pageHtml, { data });
}
